<template>
  <div class="sc-page" :style="tema">
    <!-- Carregando / não encontrada -->
    <div v-if="loading" class="sc-center">
      <div class="spinner-border sc-spinner" role="status">
        <span class="visually-hidden">Carregando…</span>
      </div>
    </div>

    <div v-else-if="naoEncontrada" class="sc-center text-center px-3">
      <div class="sc-empty-icon"><i class="bi bi-shop"></i></div>
      <h2 class="h4 mt-3">Vitrine não encontrada</h2>
      <p class="text-muted">Confira o link com a locadora.</p>
    </div>

    <!-- Pedido enviado -->
    <div v-else-if="enviado" class="sc-center px-3">
      <div class="sc-success text-center">
        <div class="sc-success-icon"><i class="bi bi-check-lg"></i></div>
        <h2 class="h3 mt-3 mb-2">Pedido <span class="sc-code">{{ enviado.code }}</span> registrado!</h2>
        <template v-if="enviado.whatsapp_url">
          <p class="text-muted">
            Agora é só enviar o pedido pelo WhatsApp para a <strong>{{ loja.name }}</strong> confirmar.
            O pedido ainda não é uma reserva: a locadora confirma a disponibilidade e o pagamento com você.
          </p>
          <p v-if="redirecionando" class="fw-semibold" role="status">
            <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>Abrindo o WhatsApp…
          </p>
          <a :href="enviado.whatsapp_url" rel="noopener" class="btn sc-btn-whats btn-lg w-100" @click="cancelarRedirecionamento">
            <i class="bi bi-whatsapp me-2"></i>{{ redirecionando ? 'Abrir agora' : 'Enviar pelo WhatsApp' }}
          </a>
        </template>
        <p v-else class="text-muted">
          O pedido ainda não é uma reserva: a <strong>{{ loja.name }}</strong> vai entrar em contato pelo telefone informado
          para confirmar a disponibilidade e o pagamento.
        </p>
        <button class="btn btn-link mt-3" @click="novoPedido">Fazer outro pedido</button>
      </div>
    </div>

    <template v-else>
      <!-- Topo -->
      <header class="sc-hero">
        <div class="sc-hero-inner container">
          <div class="d-flex align-items-center gap-3">
            <div class="sc-logo-wrap">
              <img v-if="loja.logo && !fotoQuebrada.logo" :src="loja.logo" :alt="loja.name" class="sc-logo" @error="fotoQuebrada.logo = true">
              <span v-else class="sc-logo-fallback">{{ iniciais }}</span>
            </div>
            <div class="min-w-0">
              <h1 class="sc-title">{{ loja.name }}</h1>
              <p class="sc-subtitle mb-0">Aluguel para festas e eventos</p>
            </div>
          </div>
          <p v-if="loja.showcase_message" class="sc-message sc-pre">{{ loja.showcase_message }}</p>
          <div class="sc-perks">
            <span v-if="loja.pix_discount_percent > 0" class="sc-perk"><i class="bi bi-qr-code"></i>{{ Number(loja.pix_discount_percent) }}% off no Pix</span>
            <span v-if="loja.freight_enabled" class="sc-perk"><i class="bi bi-truck"></i>Entrega com frete na hora</span>
            <span v-if="loja.has_whatsapp" class="sc-perk"><i class="bi bi-whatsapp"></i>Confirmação pelo WhatsApp</span>
          </div>
        </div>
      </header>

      <main class="container sc-main" :class="{ 'sc-has-cart': totalItens > 0 && etapa === 'catalogo' }">
        <!-- Passos -->
        <ol class="sc-steps" aria-label="Etapas do pedido">
          <li :class="{ 'is-done': passo > 1, 'is-current': passo === 1 }"><span>1</span>Data da festa</li>
          <li :class="{ 'is-done': passo > 2, 'is-current': passo === 2 }"><span>2</span>Escolha os itens</li>
          <li :class="{ 'is-current': passo === 3 }"><span>3</span>Envie o pedido</li>
        </ol>

        <template v-if="etapa === 'catalogo'">
          <!-- Data da festa -->
          <section class="sc-date-card">
            <div class="sc-date-head">
              <div class="sc-date-icon"><i class="bi bi-calendar-heart"></i></div>
              <div>
                <h2 class="h5 mb-0">Quando é a festa?</h2>
                <p class="small text-muted mb-0">{{ disponibilidade ? 'Mostrando o que está livre em ' + resumoData + '.' : 'Escolha a data e o horário para ver o que está livre.' }}</p>
              </div>
            </div>
            <form class="row g-2 align-items-end" @submit.prevent="consultar">
              <div class="col-12 col-md-4">
                <label class="form-label sc-label" for="dtData">Data</label>
                <input v-model="periodo.data" :min="hoje" required type="date" class="form-control sc-input" id="dtData">
              </div>
              <div class="col-6 col-md-3">
                <label class="form-label sc-label" for="hrInicio">Começa</label>
                <input v-model="periodo.inicio" required type="time" class="form-control sc-input" id="hrInicio">
              </div>
              <div class="col-6 col-md-3">
                <label class="form-label sc-label" for="hrFim">Termina</label>
                <input v-model="periodo.fim" required type="time" class="form-control sc-input" id="hrFim">
              </div>
              <div class="col-12 col-md-2">
                <button type="submit" class="btn sc-btn-primary w-100" :disabled="consultando">
                  <span v-if="consultando" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  <span v-else>{{ disponibilidade ? 'Atualizar' : 'Ver disponíveis' }}</span>
                </button>
              </div>
            </form>
          </section>

          <!-- Combos -->
          <section v-if="combos.length" class="mb-5">
            <div class="sc-section-head">
              <h2 class="sc-section-title"><i class="bi bi-stars"></i>Combos</h2>
              <span class="text-muted small">Pacotes prontos com preço especial</span>
            </div>
            <div class="row g-3">
              <div v-for="combo in combos" :key="combo.id" class="col-12 col-md-6">
                <article class="sc-combo" :class="{ 'sc-off': disponibilidade && !podeAdicionarCombo(combo) && !carrinhoCombos[combo.id] }">
                  <span v-if="economiaCombo(combo) > 0" class="sc-ribbon">Economize {{ money(economiaCombo(combo)) }}</span>
                  <div class="sc-combo-thumbs">
                    <template v-for="ci in combo.items.slice(0, 4)" :key="ci.id_item">
                      <img v-if="ci.foto_url && !fotoQuebrada['i' + ci.id_item]" :src="ci.foto_url" :alt="ci.name" class="sc-thumb" @error="fotoQuebrada['i' + ci.id_item] = true">
                      <span v-else class="sc-thumb sc-thumb-empty"><i class="bi bi-balloon"></i></span>
                    </template>
                  </div>
                  <h3 class="sc-card-title">{{ combo.name }}</h3>
                  <p class="small text-muted mb-1">{{ combo.items.map(ci => ci.quantity + 'x ' + ci.name).join(' + ') }}</p>
                  <p v-if="combo.description" class="small text-muted mb-2 sc-desc">{{ combo.description }}</p>
                  <div class="sc-card-foot">
                    <div>
                      <small v-if="valorAvulsoCombo(combo) > Number(combo.price)" class="text-muted text-decoration-line-through d-block">{{ money(valorAvulsoCombo(combo)) }}</small>
                      <span class="sc-price">{{ money(combo.price) }}</span>
                    </div>
                    <template v-if="disponibilidade">
                      <div v-if="carrinhoCombos[combo.id]" class="sc-stepper" role="group" :aria-label="'Quantidade de ' + combo.name">
                        <button type="button" @click="alterarCombo(combo, -1)" aria-label="Diminuir"><i class="bi bi-dash"></i></button>
                        <span>{{ carrinhoCombos[combo.id] }}</span>
                        <button type="button" @click="alterarCombo(combo, 1)" :disabled="!podeAdicionarCombo(combo)" aria-label="Aumentar"><i class="bi bi-plus"></i></button>
                      </div>
                      <span v-else-if="!podeAdicionarCombo(combo)" class="sc-pill-off">Indisponível na data</span>
                      <button v-else type="button" class="btn sc-btn-add" @click="alterarCombo(combo, 1)"><i class="bi bi-plus-lg me-1"></i>Adicionar</button>
                    </template>
                  </div>
                </article>
              </div>
            </div>
          </section>

          <!-- Catálogo -->
          <section ref="catalogoEl" class="sc-catalog">
            <div class="sc-section-head">
              <h2 class="sc-section-title"><i class="bi bi-balloon-heart"></i>Catálogo</h2>
              <span class="text-muted small">{{ itensFiltrados.length }} {{ itensFiltrados.length === 1 ? 'item' : 'itens' }}</span>
            </div>

            <div class="sc-search">
              <i class="bi bi-search" aria-hidden="true"></i>
              <input v-model="busca" type="search" class="form-control sc-input" placeholder="Buscar no catálogo" aria-label="Buscar no catálogo">
              <button v-if="busca" type="button" class="sc-search-clear" @click="busca = ''" aria-label="Limpar busca"><i class="bi bi-x-lg"></i></button>
            </div>

            <nav v-if="categorias.length > 1" class="sc-cats" aria-label="Categorias">
              <button type="button" class="sc-cat" :class="{ active: categoria === null }" @click="categoria = null">Todos</button>
              <button v-for="c in categorias" :key="c.id" type="button" class="sc-cat" :class="{ active: categoria === c.id }" @click="categoria = c.id">
                {{ c.name }}
              </button>
            </nav>

            <div class="row g-3">
              <div v-for="item in itensPagina" :key="item.id" class="col-6 col-md-4 col-xl-3">
                <article class="sc-item" :class="{ 'sc-off': disponibilidade && restante(item.id) === 0 && !carrinho[item.id] }">
                  <div class="sc-img-wrap">
                    <img v-if="item.foto_url && !fotoQuebrada['i' + item.id]" :src="item.foto_url" :alt="item.name" class="sc-img" loading="lazy" @error="fotoQuebrada['i' + item.id] = true">
                    <div v-else class="sc-img sc-img-empty"><i class="bi bi-image"></i></div>
                    <span v-if="disponibilidade" class="sc-stock" :class="{ 'is-out': restante(item.id) === 0 && !carrinho[item.id] }">
                      {{ restante(item.id) === 0 && !carrinho[item.id] ? 'Esgotado na data' : restante(item.id) + ' disponíve' + (restante(item.id) === 1 ? 'l' : 'is') }}
                    </span>
                    <span v-if="carrinho[item.id]" class="sc-in-cart"><i class="bi bi-bag-check-fill me-1"></i>{{ carrinho[item.id] }}</span>
                  </div>
                  <div class="sc-item-body">
                    <h3 class="sc-card-title">{{ item.name }}</h3>
                    <p v-if="item.description" class="small text-muted mb-2 sc-desc">{{ item.description }}</p>
                    <div class="sc-card-foot">
                      <div>
                        <small v-if="!disponibilidade && item.price_rules.length" class="text-muted d-block">a partir de</small>
                        <span class="sc-price">{{ money(precoExibido(item)) }}</span>
                        <small v-if="disponibilidade && duracao" class="text-muted"> / {{ duracao }}h</small>
                      </div>
                      <template v-if="disponibilidade">
                        <div v-if="carrinho[item.id]" class="sc-stepper" role="group" :aria-label="'Quantidade de ' + item.name">
                          <button type="button" @click="alterar(item, -1)" aria-label="Diminuir"><i class="bi bi-dash"></i></button>
                          <span>{{ carrinho[item.id] }}</span>
                          <button type="button" @click="alterar(item, 1)" :disabled="restante(item.id) === 0" aria-label="Aumentar"><i class="bi bi-plus"></i></button>
                        </div>
                        <button v-else-if="restante(item.id) > 0" type="button" class="sc-btn-round" @click="alterar(item, 1)" :aria-label="'Adicionar ' + item.name">
                          <i class="bi bi-plus-lg"></i>
                        </button>
                      </template>
                    </div>
                  </div>
                </article>
              </div>
              <div v-if="itensFiltrados.length === 0" class="col-12 text-center text-muted py-4">
                <i class="bi bi-search d-block fs-2 mb-2"></i>
                {{ busca ? 'Nenhum item encontrado para "' + busca + '".' : 'Nenhum item nesta categoria.' }}
              </div>
            </div>

            <!-- Paginação -->
            <nav v-if="totalPaginas > 1" class="sc-pager" aria-label="Páginas do catálogo">
              <button type="button" class="sc-page-btn" :disabled="pagina === 1" @click="irPara(pagina - 1)" aria-label="Página anterior"><i class="bi bi-chevron-left"></i></button>
              <template v-for="n in paginasVisiveis" :key="n">
                <span v-if="n === '…'" class="sc-page-gap">…</span>
                <button v-else type="button" class="sc-page-btn" :class="{ active: n === pagina }" :aria-current="n === pagina ? 'page' : null" @click="irPara(n)">{{ n }}</button>
              </template>
              <button type="button" class="sc-page-btn" :disabled="pagina === totalPaginas" @click="irPara(pagina + 1)" aria-label="Próxima página"><i class="bi bi-chevron-right"></i></button>
            </nav>
          </section>
        </template>

        <!-- Dados do pedido -->
        <section v-else class="sc-checkout">
          <button type="button" class="btn btn-link px-0 mb-3 sc-back" @click="etapa = 'catalogo'"><i class="bi bi-arrow-left me-1"></i>Voltar ao catálogo</button>

          <div class="row g-4">
            <div class="col-lg-7 order-2 order-lg-1">
              <form class="sc-panel" @submit.prevent="enviar">
                <h2 class="h5 mb-3"><i class="bi bi-person-heart me-2 sc-accent"></i>Seus dados</h2>
                <div class="row g-3">
                  <div class="col-12">
                    <label class="form-label sc-label" for="txtNome">Nome</label>
                    <input v-model="cliente.customer_name" required maxlength="255" class="form-control sc-input" id="txtNome" autocomplete="name">
                  </div>
                  <div class="col-md-6">
                    <label class="form-label sc-label" for="txtFone">WhatsApp</label>
                    <input v-model="cliente.customer_phone" required maxlength="20" type="tel" class="form-control sc-input" id="txtFone" placeholder="(51) 99999-9999" autocomplete="tel">
                  </div>
                  <div class="col-md-6">
                    <label class="form-label sc-label" for="txtEmail">E-mail <span class="text-muted fw-normal">(opcional)</span></label>
                    <input v-model="cliente.customer_email" maxlength="255" type="email" class="form-control sc-input" id="txtEmail" autocomplete="email">
                  </div>
                </div>

                <h2 class="h5 mt-4 mb-3"><i class="bi bi-geo-alt me-2 sc-accent"></i>Entrega</h2>
                <div class="sc-options" role="radiogroup" aria-label="Entrega ou retirada">
                  <label class="sc-option" :class="{ active: !cliente.withdrawal }">
                    <input v-model="cliente.withdrawal" :value="false" type="radio" name="entrega" class="visually-hidden" @change="cotarFrete">
                    <i class="bi bi-truck"></i>
                    <span><strong>Entregar na festa</strong><small>{{ loja.freight_enabled ? 'Frete calculado pelo CEP' : 'Frete combinado com a locadora' }}</small></span>
                  </label>
                  <label class="sc-option" :class="{ active: cliente.withdrawal }">
                    <input v-model="cliente.withdrawal" :value="true" type="radio" name="entrega" class="visually-hidden" @change="cotarFrete">
                    <i class="bi bi-shop"></i>
                    <span><strong>Vou retirar</strong><small>Sem frete</small></span>
                  </label>
                </div>

                <div v-if="!cliente.withdrawal" class="row g-3 mt-1">
                  <div class="col-md-4">
                    <label class="form-label sc-label" for="txtCep">CEP</label>
                    <input v-model="cliente.zipcode" @blur="buscarCep" maxlength="10" class="form-control sc-input" id="txtCep" placeholder="00000-000" inputmode="numeric" autocomplete="postal-code">
                  </div>
                  <div class="col-md-8">
                    <label class="form-label sc-label" for="txtEnd">Endereço da festa</label>
                    <input v-model="cliente.address" required maxlength="255" class="form-control sc-input" id="txtEnd" autocomplete="address-line1">
                  </div>
                  <div class="col-md-3">
                    <label class="form-label sc-label" for="txtNum">Número</label>
                    <input v-model="cliente.number" @blur="cotarFrete" maxlength="20" class="form-control sc-input" id="txtNum">
                  </div>
                  <div class="col-md-4">
                    <label class="form-label sc-label" for="txtBairro">Bairro</label>
                    <input v-model="cliente.neighbor" maxlength="255" class="form-control sc-input" id="txtBairro">
                  </div>
                  <div class="col-md-3">
                    <label class="form-label sc-label" for="txtCidade">Cidade</label>
                    <input v-model="cliente.city" required maxlength="255" class="form-control sc-input" id="txtCidade" autocomplete="address-level2">
                  </div>
                  <div class="col-md-2">
                    <label class="form-label sc-label" for="txtUf">UF</label>
                    <input v-model="cliente.state" maxlength="2" class="form-control sc-input text-uppercase" id="txtUf" autocomplete="address-level1">
                  </div>
                </div>

                <template v-if="loja.pix_discount_percent > 0">
                  <h2 class="h5 mt-4 mb-3"><i class="bi bi-wallet2 me-2 sc-accent"></i>Pagamento</h2>
                  <div class="sc-options" role="radiogroup" aria-label="Forma de pagamento">
                    <label class="sc-option" :class="{ active: cliente.payment_method === 'PIX' }">
                      <input v-model="cliente.payment_method" type="radio" value="PIX" name="pagamento" class="visually-hidden">
                      <i class="bi bi-qr-code"></i>
                      <span><strong>Pix</strong><small class="text-success">{{ Number(loja.pix_discount_percent) }}% de desconto</small></span>
                    </label>
                    <label class="sc-option" :class="{ active: cliente.payment_method === 'OTHER' }">
                      <input v-model="cliente.payment_method" type="radio" value="OTHER" name="pagamento" class="visually-hidden">
                      <i class="bi bi-credit-card"></i>
                      <span><strong>Outra forma</strong><small>Combinar com a locadora</small></span>
                    </label>
                  </div>
                </template>

                <div class="mt-4">
                  <label class="form-label sc-label" for="txtObs">Observações <span class="text-muted fw-normal">(opcional)</span></label>
                  <textarea v-model="cliente.note" maxlength="2000" rows="2" class="form-control sc-input" id="txtObs" placeholder="Ex.: tema da festa, acesso ao local, horário de montagem"></textarea>
                </div>

                <button type="submit" class="btn sc-btn-whats btn-lg w-100 mt-4" :disabled="enviando">
                  <span v-if="enviando" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  <i v-else class="bi bi-whatsapp me-2"></i>Enviar pedido
                </button>
                <p class="small text-muted text-center mt-2 mb-0">O pedido não é uma reserva: a locadora confirma a disponibilidade e o pagamento com você.</p>
              </form>
            </div>

            <!-- Resumo -->
            <aside class="col-lg-5 order-1 order-lg-2">
              <div class="sc-panel sc-summary">
                <h2 class="h5 mb-1">Seu pedido</h2>
                <p class="small text-muted mb-3"><i class="bi bi-calendar-event me-1"></i>{{ resumoData }}</p>
                <div v-for="linha in linhasCombos" :key="'c' + linha.combo.id" class="sc-line">
                  <span><span class="sc-qty-chip">{{ linha.quantity }}x</span>Combo {{ linha.combo.name }}</span>
                  <span>{{ money(linha.quantity * linha.combo.price) }}</span>
                </div>
                <div v-for="linha in linhasCarrinho" :key="linha.item.id" class="sc-line">
                  <span><span class="sc-qty-chip">{{ linha.quantity }}x</span>{{ linha.item.name }}</span>
                  <span>{{ money(linha.quantity * preco(linha.item)) }}</span>
                </div>
                <hr>
                <div v-if="!cliente.withdrawal && loja.freight_enabled" class="sc-line">
                  <span>Frete</span>
                  <span v-if="frete.carregando" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  <span v-else-if="frete.regra === null" class="text-muted">informe o CEP</span>
                  <span v-else-if="frete.valor === null" class="text-muted">a combinar</span>
                  <span v-else-if="frete.valor === 0" class="text-success fw-semibold">grátis</span>
                  <span v-else>{{ money(frete.valor) }}</span>
                </div>
                <div v-if="descontoPix > 0" class="sc-line text-success">
                  <span>Desconto Pix ({{ Number(loja.pix_discount_percent) }}%)</span><span>-{{ money(descontoPix) }}</span>
                </div>
                <div class="sc-total">
                  <span>Total estimado</span><span>{{ money(totalValor - descontoPix + freteCobrado) }}</span>
                </div>
                <p class="small text-muted mb-0 mt-2">{{ loja.freight_enabled ? 'O frete é calculado pelo CEP da festa e confirmado pela locadora.' : 'Frete e condições são combinados com a locadora.' }}</p>
              </div>
            </aside>
          </div>
        </section>
      </main>

      <footer class="sc-footer">
        Vitrine feita com <strong>AlugUp</strong>
      </footer>

      <!-- Barra do carrinho -->
      <div v-if="totalItens > 0 && etapa === 'catalogo'" class="sc-cart">
        <div class="sc-cart-inner">
          <div class="d-flex align-items-center gap-3">
            <span class="sc-cart-icon"><i class="bi bi-bag-heart"></i><span class="sc-cart-count">{{ totalItens }}</span></span>
            <div>
              <div class="small sc-cart-label">{{ totalItens }} {{ totalItens === 1 ? 'item' : 'itens' }} no pedido</div>
              <strong class="sc-cart-total">{{ money(totalValor) }}</strong>
            </div>
          </div>
          <button type="button" class="btn sc-btn-primary" @click="etapa = 'dados'">Continuar<i class="bi bi-arrow-right ms-2"></i></button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { onMounted, onBeforeUnmount, ref, reactive, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { showcase } from '@/models/showcase';
import { useToastStore } from '@/stores/toastStore';
import { parseError } from '@/utils/errorHelper';
import { temaVitrine } from '@/utils/showcaseTheme';

export default {
  name: "ShowcaseView",
  setup() {

    const route = useRoute();
    const toastStore = useToastStore();
    const model = showcase();
    const slug = route.params.slug;

    const loading = ref(true);
    const naoEncontrada = ref(false);
    const consultando = ref(false);
    const enviando = ref(false);
    const enviado = ref(null);
    const etapa = ref('catalogo'); // catalogo | dados

    const loja = ref({});
    const categorias = ref([]);
    const itens = ref([]);
    const categoria = ref(null);
    const combos = ref([]);
    const disponibilidade = ref(null); // { idItem: quantidade }
    const precos = ref({});            // { idItem: preço para a duração consultada }
    const duracao = ref(null);         // horas consultadas
    const carrinho = reactive({});      // { idItem: quantidade }
    const carrinhoCombos = reactive({}); // { idCombo: quantidade }
    const fotoQuebrada = reactive({});   // imagens que falharam ao carregar → mostra o ícone

    // Data local (toISOString usaria UTC e viraria o dia à noite)
    const hoje = new Date().toLocaleDateString('sv-SE');
    const periodo = reactive({ data: '', inicio: '14:00', fim: '18:00' });
    // Período efetivamente consultado (o cliente pode mexer nos campos depois)
    const consultado = reactive({ start: null, end: null });

    const clienteVazio = () => ({
      customer_name: '', customer_phone: '', customer_email: '', withdrawal: false,
      zipcode: '', address: '', number: '', neighbor: '', city: '', state: '', note: '',
      payment_method: 'PIX'
    });
    const cliente = reactive(clienteVazio());

    // Busca sem acento/maiúscula, por nome e descrição
    const busca = ref('');
    const normalizar = t => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

    const itensFiltrados = computed(() => {
      const termo = normalizar(busca.value.trim());
      return itens.value.filter(i =>
        (categoria.value === null || i.id_category === categoria.value)
        && (!termo || normalizar(i.name + ' ' + (i.description || '')).includes(termo))
      );
    });

    // Paginação do catálogo
    const POR_PAGINA = 12;
    const pagina = ref(1);
    const catalogoEl = ref(null);
    const totalPaginas = computed(() => Math.max(1, Math.ceil(itensFiltrados.value.length / POR_PAGINA)));
    const itensPagina = computed(() => itensFiltrados.value.slice((pagina.value - 1) * POR_PAGINA, pagina.value * POR_PAGINA));

    // Primeira, última e vizinhas da atual; "…" nos saltos
    const paginasVisiveis = computed(() => {
      const total = totalPaginas.value;
      const atual = pagina.value;
      const lista = [];
      for (let n = 1; n <= total; n++) {
        if (n === 1 || n === total || Math.abs(n - atual) <= 1) lista.push(n);
        else if (lista[lista.length - 1] !== '…') lista.push('…');
      }
      return lista;
    });

    function irPara(n) {
      pagina.value = Math.min(Math.max(1, n), totalPaginas.value);
      catalogoEl.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    watch([busca, categoria], () => { pagina.value = 1; });

    const linhasCarrinho = computed(() =>
      itens.value.filter(i => carrinho[i.id] > 0).map(i => ({ item: i, quantity: carrinho[i.id] }))
    );
    const linhasCombos = computed(() =>
      combos.value.filter(c => carrinhoCombos[c.id] > 0).map(c => ({ combo: c, quantity: carrinhoCombos[c.id] }))
    );
    const totalItens = computed(() =>
      linhasCarrinho.value.reduce((s, l) => s + l.quantity, 0) + linhasCombos.value.reduce((s, l) => s + l.quantity, 0)
    );
    const totalValor = computed(() =>
      linhasCarrinho.value.reduce((s, l) => s + l.quantity * preco(l.item), 0)
      + linhasCombos.value.reduce((s, l) => s + l.quantity * Number(l.combo.price), 0)
    );
    // Mesmo cálculo do backend (PricingService::pixDiscount); o valor final é o do servidor
    // Frete cotado pelo CEP (prévia; o valor final é recalculado no servidor ao enviar)
    const frete = reactive({ valor: null, regra: null, carregando: false });
    const freteCobrado = computed(() =>
      !cliente.withdrawal && loja.value.freight_enabled ? (frete.valor || 0) : 0
    );

    const descontoPix = computed(() =>
      cliente.payment_method === 'PIX' && loja.value.pix_discount_percent > 0
        ? Math.round(totalValor.value * loja.value.pix_discount_percent) / 100
        : 0
    );

    const tema = computed(() => temaVitrine(loja.value.showcase_color));

    // Etapa atual para o indicador de passos (1 data, 2 itens, 3 dados)
    const passo = computed(() => etapa.value === 'dados' ? 3 : (disponibilidade.value ? 2 : 1));

    const iniciais = computed(() => (loja.value.name || '')
      .split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join(''));

    const resumoData = computed(() => {
      if (!consultado.start) return '';
      const [d, h1] = consultado.start.split(' ');
      const h2 = consultado.end.split(' ')[1];
      return `${d.split('-').reverse().join('/')}, das ${h1.slice(0, 5)} às ${h2.slice(0, 5)}`;
    });

    // Preço do item para a duração consultada (preço por período), ou o preço por evento
    function preco(item) {
      return Number(precos.value[item.id] ?? item.unit_price);
    }

    // Antes de escolher a data: menor preço possível ("a partir de")
    function precoExibido(item) {
      if (disponibilidade.value) return preco(item);
      return Math.min(Number(item.unit_price), ...item.price_rules.map(r => Number(r.price)));
    }

    // Estoque livre do item descontando o que já está no carrinho (avulso + dentro de combos)
    function restante(idItem) {
      let usado = carrinho[idItem] || 0;
      combos.value.forEach(c => {
        const ci = c.items.find(x => x.id_item === idItem);
        if (ci) usado += (carrinhoCombos[c.id] || 0) * ci.quantity;
      });
      return Math.max(0, (disponibilidade.value?.[idItem] ?? 0) - usado);
    }

    function podeAdicionarCombo(combo) {
      return combo.items.length > 0 && combo.items.every(ci => restante(ci.id_item) >= ci.quantity);
    }

    function alterar(item, delta) {
      if (delta > 0 && restante(item.id) === 0) return;
      const nova = Math.max(0, (carrinho[item.id] || 0) + delta);
      if (nova === 0) delete carrinho[item.id];
      else carrinho[item.id] = nova;
    }

    function alterarCombo(combo, delta) {
      if (delta > 0 && !podeAdicionarCombo(combo)) return;
      const nova = Math.max(0, (carrinhoCombos[combo.id] || 0) + delta);
      if (nova === 0) delete carrinhoCombos[combo.id];
      else carrinhoCombos[combo.id] = nova;
    }

    function valorAvulsoCombo(combo) {
      const porId = Object.fromEntries(itens.value.map(i => [i.id, i]));
      return combo.items.reduce((s, ci) => s + (porId[ci.id_item] ? preco(porId[ci.id_item]) * ci.quantity : 0), 0);
    }

    // Quanto o combo sai mais barato que os itens avulsos na mesma duração
    function economiaCombo(combo) {
      return Math.max(0, valorAvulsoCombo(combo) - Number(combo.price));
    }

    function limparCarrinho() {
      Object.keys(carrinho).forEach(id => delete carrinho[id]);
      Object.keys(carrinhoCombos).forEach(id => delete carrinhoCombos[id]);
    }

    async function consultar() {
      if (periodo.fim <= periodo.inicio) {
        toastStore.warning('O horário de fim precisa ser depois do início.');
        return;
      }
      consultando.value = true;
      try {
        const start = `${periodo.data} ${periodo.inicio}:00`;
        const end = `${periodo.data} ${periodo.fim}:00`;
        const res = await model.availability(slug, start, end);
        const mudouData = consultado.start && (consultado.start !== start || consultado.end !== end);
        disponibilidade.value = res.items;
        precos.value = res.prices;
        duracao.value = res.hours;
        consultado.start = start;
        consultado.end = end;
        // Nova data: o que estava no carrinho pode não estar mais livre
        if (mudouData && totalItens.value > 0) {
          limparCarrinho();
          toastStore.warning('Data alterada: monte o pedido novamente.');
        }
      } catch (error) {
        toastStore.error(parseError(error));
      }
      consultando.value = false;
    }

    async function buscarCep() {
      const cep = (cliente.zipcode || '').replace(/\D/g, '');
      if (cep.length !== 8) return;
      try {
        const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await res.json();
        if (data.erro) return;
        cliente.address = cliente.address || data.logradouro;
        cliente.neighbor = cliente.neighbor || data.bairro;
        cliente.city = data.localidade;
        cliente.state = data.uf;
      } catch (e) {
        // CEP é só ajuda de preenchimento; ignora falhas
      }
      cotarFrete();
    }

    async function cotarFrete() {
      if (cliente.withdrawal || !loja.value.freight_enabled) return;
      if ((cliente.zipcode || '').replace(/\D/g, '').length !== 8) return;
      frete.carregando = true;
      try {
        const res = await model.freight(slug, {
          zipcode: cliente.zipcode,
          number: cliente.number || undefined,
          address: cliente.address || undefined,
          city: cliente.city || undefined,
          state: cliente.state ? cliente.state.toUpperCase() : undefined,
          subtotal: totalValor.value,
        });
        frete.valor = res.freight === null ? null : Number(res.freight);
        frete.regra = res.rule;
      } catch (e) {
        frete.valor = null;
        frete.regra = 'unavailable';
      }
      frete.carregando = false;
    }

    const redirecionando = ref(false);
    let timerWhats = null;

    async function enviar() {
      enviando.value = true;
      try {
        const payload = {
          ...cliente,
          state: cliente.state ? cliente.state.toUpperCase() : null,
          event_date_start: consultado.start,
          event_date_end: consultado.end,
          payment_method: loja.value.pix_discount_percent > 0 ? cliente.payment_method : null,
          items: linhasCarrinho.value.map(l => ({ id: l.item.id, quantity: l.quantity })),
          combos: linhasCombos.value.map(l => ({ id: l.combo.id, quantity: l.quantity })),
        };
        enviado.value = await model.storeOrder(slug, payload);
        // Mostra a confirmação por um instante e segue para o WhatsApp na mesma aba
        // (abrir em nova aba depois de uma requisição é bloqueado como pop-up)
        if (enviado.value.whatsapp_url) {
          redirecionando.value = true;
          timerWhats = setTimeout(() => {
            redirecionando.value = false;
            window.location.assign(enviado.value.whatsapp_url);
          }, 2000);
        }
      } catch (error) {
        toastStore.error(parseError(error));
        // Algo ficou indisponível enquanto o cliente preenchia: atualiza a disponibilidade
        if (error.response?.data?.errors?.unavailable_items) {
          etapa.value = 'catalogo';
          await consultar();
        }
      }
      enviando.value = false;
    }

    function cancelarRedirecionamento() {
      clearTimeout(timerWhats);
      redirecionando.value = false;
    }

    function novoPedido() {
      cancelarRedirecionamento();
      limparCarrinho();
      Object.assign(cliente, clienteVazio());
      Object.assign(frete, { valor: null, regra: null, carregando: false });
      enviado.value = null;
      etapa.value = 'catalogo';
    }

    function money(valor) {
      return Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }

    onBeforeUnmount(cancelarRedirecionamento);

    // Troca de etapa começa do topo (no celular o formulário fica abaixo da dobra)
    watch(etapa, () => window.scrollTo({ top: 0, behavior: 'smooth' }));

    onMounted(async () => {
      try {
        const data = await model.getOne(slug);
        loja.value = data.tenant;
        categorias.value = data.categories;
        itens.value = data.items;
        combos.value = data.combos;
        document.title = data.tenant.name;
      } catch (error) {
        naoEncontrada.value = true;
      }
      loading.value = false;
    });

    return {
      loading, naoEncontrada, consultando, enviando, enviado, etapa,
      loja, categorias, itens, combos, categoria, disponibilidade, duracao, carrinho, carrinhoCombos, periodo, cliente, hoje,
      itensFiltrados, itensPagina, busca, pagina, totalPaginas, paginasVisiveis, irPara, catalogoEl, linhasCarrinho, linhasCombos, totalItens, totalValor, descontoPix, resumoData, frete, freteCobrado, cotarFrete,
      preco, precoExibido, restante, podeAdicionarCombo, alterar, alterarCombo, valorAvulsoCombo, economiaCombo, passo, iniciais, tema, fotoQuebrada,
      consultar, buscarCep, enviar, novoPedido, money, redirecionando, cancelarRedirecionamento
    }
  }
}
</script>

<style scoped>
.sc-page {
  --sc-accent: #7c3aed;
  --sc-accent-2: #ec4899;
  --sc-accent-soft: rgba(124, 58, 237, .12);
  --sc-accent-ring: rgba(124, 58, 237, .2);
  --sc-ink: #1f1633;
  --sc-muted: #6b6480;
  --sc-line: #ece7f5;
  --sc-bg: #faf8fe;
  --sc-whats: #22c55e;
  --sc-radius: 18px;
  min-height: 100vh;
  background: var(--sc-bg);
  color: var(--sc-ink);
}

.sc-center {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.sc-spinner {
  width: 3rem;
  height: 3rem;
  color: var(--sc-accent);
}

.sc-empty-icon {
  width: 84px;
  height: 84px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--sc-accent-soft);
  color: var(--sc-accent);
  font-size: 2.4rem;
}

.sc-pre {
  white-space: pre-line;
}

.sc-accent {
  color: var(--sc-accent);
}

/* Topo */
.sc-hero {
  position: relative;
  color: #fff;
  background:
    radial-gradient(circle at 12% 20%, rgba(255, 255, 255, .18) 0 3px, transparent 4px),
    radial-gradient(circle at 78% 30%, rgba(255, 255, 255, .22) 0 4px, transparent 5px),
    radial-gradient(circle at 60% 75%, rgba(255, 255, 255, .16) 0 3px, transparent 4px),
    radial-gradient(circle at 90% 85%, rgba(255, 255, 255, .2) 0 2px, transparent 3px),
    radial-gradient(circle at 30% 80%, rgba(255, 255, 255, .14) 0 5px, transparent 6px),
    linear-gradient(135deg, var(--sc-accent) 0%, var(--sc-accent-2) 100%);
  padding: 36px 0 88px;
  overflow: hidden;
}

.sc-hero::after {
  content: "";
  position: absolute;
  right: -80px;
  top: -80px;
  width: 260px;
  height: 260px;
  border-radius: 50%;
  background: rgba(255, 255, 255, .08);
}

.sc-hero-inner {
  position: relative;
  z-index: 1;
}

.sc-logo-wrap {
  flex: none;
  width: 76px;
  height: 76px;
  border-radius: 22px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(31, 22, 51, .25);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.sc-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 6px;
}

.sc-logo-fallback {
  font-weight: 800;
  font-size: 1.6rem;
  background: linear-gradient(135deg, var(--sc-accent), var(--sc-accent-2));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.min-w-0 {
  min-width: 0;
}

.sc-title {
  font-size: clamp(1.5rem, 4vw, 2.2rem);
  font-weight: 800;
  letter-spacing: -.02em;
  margin: 0;
  line-height: 1.1;
}

.sc-subtitle {
  opacity: .85;
}

.sc-message {
  margin: 16px 0 0;
  max-width: 720px;
  font-size: 1.02rem;
  opacity: .95;
}

.sc-perks {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 18px;
}

.sc-perk {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, .18);
  border: 1px solid rgba(255, 255, 255, .3);
  backdrop-filter: blur(4px);
  border-radius: 999px;
  padding: 5px 12px;
  font-size: .85rem;
  font-weight: 600;
}

/* Conteúdo */
.sc-main {
  position: relative;
  margin-top: -64px;
  padding-bottom: 32px;
}

.sc-has-cart {
  padding-bottom: 120px !important;
}

.sc-steps {
  list-style: none;
  display: flex;
  gap: 8px;
  padding: 0;
  margin: 0 0 14px;
  overflow-x: auto;
}

.sc-steps li {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  background: rgba(255, 255, 255, .92);
  border-radius: 999px;
  padding: 6px 14px 6px 6px;
  font-size: .85rem;
  font-weight: 600;
  color: var(--sc-muted);
  box-shadow: 0 2px 8px rgba(31, 22, 51, .08);
}

.sc-steps li span {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--sc-line);
  font-size: .78rem;
}

.sc-steps li.is-current {
  color: var(--sc-ink);
}

.sc-steps li.is-current span {
  background: linear-gradient(135deg, var(--sc-accent), var(--sc-accent-2));
  color: #fff;
}

.sc-steps li.is-done span {
  background: #dcfce7;
  color: #15803d;
}

.sc-date-card,
.sc-panel {
  background: #fff;
  border-radius: var(--sc-radius);
  box-shadow: 0 12px 32px rgba(31, 22, 51, .08);
  border: 1px solid var(--sc-line);
  padding: 22px;
}

.sc-date-card {
  margin-bottom: 36px;
}

.sc-date-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.sc-date-icon {
  flex: none;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: var(--sc-accent-soft);
  color: var(--sc-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
}

.sc-label {
  font-size: .82rem;
  font-weight: 600;
  color: var(--sc-muted);
  margin-bottom: 4px;
}

.sc-input {
  border-radius: 12px;
  border-color: var(--sc-line);
  padding: 10px 12px;
}

.sc-input:focus {
  border-color: var(--sc-accent);
  box-shadow: 0 0 0 .2rem var(--sc-accent-ring);
}

/* Botões */
.sc-btn-primary {
  background: linear-gradient(135deg, var(--sc-accent), var(--sc-accent-2));
  color: #fff;
  border: 0;
  border-radius: 12px;
  font-weight: 700;
  padding: 10px 18px;
}

.sc-btn-primary:hover,
.sc-btn-primary:focus {
  color: #fff;
  filter: brightness(1.05);
}

.sc-btn-whats {
  background: var(--sc-whats);
  color: #fff;
  border: 0;
  border-radius: 14px;
  font-weight: 700;
}

.sc-btn-whats:hover,
.sc-btn-whats:focus {
  color: #fff;
  background: #16a34a;
}

.sc-btn-add {
  background: var(--sc-accent-soft);
  color: var(--sc-accent);
  border: 0;
  border-radius: 999px;
  font-weight: 700;
  font-size: .9rem;
  padding: 6px 14px;
}

.sc-btn-add:hover {
  background: var(--sc-accent);
  color: #fff;
}

.sc-btn-round {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 0;
  background: linear-gradient(135deg, var(--sc-accent), var(--sc-accent-2));
  color: #fff;
  font-size: 1.1rem;
  box-shadow: 0 6px 14px var(--sc-accent-ring);
  transition: transform .15s;
}

.sc-btn-round:hover {
  transform: scale(1.08);
}

.sc-stepper {
  display: inline-flex;
  align-items: center;
  background: var(--sc-accent-soft);
  border-radius: 999px;
  padding: 3px;
}

.sc-stepper button {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 0;
  background: #fff;
  color: var(--sc-accent);
  font-size: 1rem;
  box-shadow: 0 1px 3px rgba(31, 22, 51, .12);
}

.sc-stepper button:disabled {
  opacity: .4;
}

.sc-stepper span {
  min-width: 2rem;
  text-align: center;
  font-weight: 700;
  color: var(--sc-accent);
}

.sc-pill-off {
  font-size: .78rem;
  font-weight: 600;
  color: var(--sc-muted);
  background: #f1f0f4;
  border-radius: 999px;
  padding: 4px 10px;
}

/* Seções */
.sc-section-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 14px;
}

.sc-section-title {
  font-size: 1.35rem;
  font-weight: 800;
  letter-spacing: -.01em;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.sc-section-title i {
  color: var(--sc-accent-2);
}

.sc-cats {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 10px 0;
  margin-bottom: 8px;
  background: linear-gradient(var(--sc-bg) 80%, rgba(250, 248, 254, 0));
  scrollbar-width: none;
}

.sc-cats::-webkit-scrollbar {
  display: none;
}

.sc-cat {
  flex: none;
  border: 1px solid var(--sc-line);
  background: #fff;
  color: var(--sc-ink);
  border-radius: 999px;
  padding: 7px 16px;
  font-weight: 600;
  font-size: .9rem;
  transition: all .15s;
}

.sc-cat:hover {
  border-color: var(--sc-accent);
}

.sc-cat.active {
  background: var(--sc-ink);
  border-color: var(--sc-ink);
  color: #fff;
}

/* Busca e paginação */
.sc-catalog {
  scroll-margin-top: 12px;
}

.sc-search {
  position: relative;
  max-width: 420px;
  margin-bottom: 4px;
}

.sc-search > i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--sc-muted);
}

.sc-search .sc-input {
  padding-left: 40px;
  padding-right: 40px;
  background: #fff;
}

.sc-search-clear {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: transparent;
  color: var(--sc-muted);
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.sc-search-clear:hover {
  background: var(--sc-accent-soft);
}

/* Esconde o "x" nativo do input search (já há o botão de limpar) */
.sc-search input::-webkit-search-cancel-button {
  -webkit-appearance: none;
}

.sc-pager {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 24px;
}

.sc-page-btn {
  min-width: 40px;
  height: 40px;
  padding: 0 10px;
  border-radius: 12px;
  border: 1px solid var(--sc-line);
  background: #fff;
  color: var(--sc-ink);
  font-weight: 700;
}

.sc-page-btn:hover:not(:disabled) {
  border-color: var(--sc-accent);
  color: var(--sc-accent);
}

.sc-page-btn.active {
  background: linear-gradient(135deg, var(--sc-accent), var(--sc-accent-2));
  border-color: transparent;
  color: #fff;
}

.sc-page-btn:disabled {
  opacity: .4;
}

.sc-page-gap {
  color: var(--sc-muted);
  padding: 0 4px;
}

/* Cartões */
.sc-item,
.sc-combo {
  position: relative;
  height: 100%;
  background: #fff;
  border-radius: var(--sc-radius);
  border: 1px solid var(--sc-line);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform .2s, box-shadow .2s, opacity .2s;
}

.sc-item:hover,
.sc-combo:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 30px rgba(31, 22, 51, .1);
}

.sc-off {
  opacity: .5;
}

.sc-off:hover {
  transform: none;
  box-shadow: none;
}

.sc-img-wrap {
  position: relative;
  aspect-ratio: 4 / 3;
  background: var(--sc-accent-soft);
}

.sc-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.sc-img-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--sc-accent);
  font-size: 2.5rem;
}

.sc-stock {
  position: absolute;
  left: 10px;
  bottom: 10px;
  background: rgba(255, 255, 255, .95);
  color: #15803d;
  font-size: .75rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 3px 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, .1);
}

.sc-stock.is-out {
  color: #b91c1c;
}

.sc-in-cart {
  position: absolute;
  right: 10px;
  top: 10px;
  background: var(--sc-accent);
  color: #fff;
  font-size: .78rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 3px 10px;
}

.sc-item-body {
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.sc-card-title {
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 4px;
}

.sc-desc {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.sc-card-foot {
  margin-top: auto;
  padding-top: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.sc-price {
  font-size: 1.15rem;
  font-weight: 800;
  color: var(--sc-ink);
}

.sc-combo {
  padding: 18px;
  border: 2px solid transparent;
  background:
    linear-gradient(#fff, #fff) padding-box,
    linear-gradient(135deg, var(--sc-accent), var(--sc-accent-2)) border-box;
}

.sc-ribbon {
  position: absolute;
  top: 14px;
  right: -1px;
  background: linear-gradient(135deg, var(--sc-accent), var(--sc-accent-2));
  color: #fff;
  font-size: .75rem;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 999px 0 0 999px;
}

.sc-combo-thumbs {
  display: flex;
  margin-bottom: 12px;
}

.sc-thumb {
  width: 58px;
  height: 58px;
  object-fit: cover;
  border-radius: 14px;
  border: 3px solid #fff;
  box-shadow: 0 2px 8px rgba(31, 22, 51, .12);
  margin-right: -10px;
  background: var(--sc-accent-soft);
}

.sc-thumb-empty {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--sc-accent);
}

/* Finalização */
.sc-back {
  color: var(--sc-accent);
  font-weight: 600;
  text-decoration: none;
}

.sc-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.sc-option {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 2px solid var(--sc-line);
  border-radius: 14px;
  padding: 12px 14px;
  cursor: pointer;
  transition: border-color .15s, background .15s;
}

.sc-option i {
  font-size: 1.4rem;
  color: var(--sc-muted);
}

.sc-option span {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}

.sc-option small {
  color: var(--sc-muted);
}

.sc-option.active {
  border-color: var(--sc-accent);
  background: var(--sc-accent-soft);
}

.sc-option.active i {
  color: var(--sc-accent);
}

.sc-option:focus-within {
  outline: 2px solid var(--sc-accent-ring);
  outline-offset: 2px;
}

.sc-summary {
  position: sticky;
  top: 16px;
}

.sc-line {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 0;
}

.sc-qty-chip {
  display: inline-block;
  min-width: 2rem;
  margin-right: 6px;
  font-weight: 700;
  color: var(--sc-accent);
}

.sc-total {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  background: var(--sc-accent-soft);
  font-weight: 800;
  font-size: 1.1rem;
}

/* Sucesso */
.sc-success {
  max-width: 520px;
  background: #fff;
  border-radius: 24px;
  padding: 36px 28px;
  box-shadow: 0 20px 50px rgba(31, 22, 51, .1);
}

.sc-success-icon {
  width: 84px;
  height: 84px;
  margin: 0 auto;
  border-radius: 50%;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.6rem;
  box-shadow: 0 0 0 10px #dcfce7;
}

.sc-code {
  color: var(--sc-accent);
}

/* Carrinho */
.sc-cart {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 16px;
  z-index: 1030;
  padding: 0 12px;
  pointer-events: none;
}

.sc-cart-inner {
  pointer-events: auto;
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  background: var(--sc-ink);
  color: #fff;
  border-radius: 20px;
  padding: 10px 10px 10px 16px;
  box-shadow: 0 16px 40px rgba(31, 22, 51, .35);
}

.sc-cart-icon {
  position: relative;
  font-size: 1.6rem;
}

.sc-cart-count {
  position: absolute;
  top: -4px;
  right: -10px;
  background: var(--sc-accent-2);
  color: #fff;
  font-size: .7rem;
  font-weight: 700;
  border-radius: 999px;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
}

.sc-cart-label {
  opacity: .75;
}

.sc-cart-total {
  font-size: 1.15rem;
}

.sc-footer {
  text-align: center;
  font-size: .8rem;
  color: var(--sc-muted);
  padding: 8px 0 28px;
}

@media (max-width: 576px) {
  .sc-hero {
    padding: 26px 0 80px;
  }

  .sc-logo-wrap {
    width: 62px;
    height: 62px;
    border-radius: 18px;
  }

  .sc-date-card,
  .sc-panel {
    padding: 18px;
  }

  .sc-options {
    grid-template-columns: 1fr;
  }

  /* Catálogo em 2 colunas no celular */
  .sc-item-body {
    padding: 10px 12px 12px;
  }

  .sc-card-title {
    font-size: .9rem;
  }

  .sc-item .sc-desc {
    display: none;
  }

  .sc-item .sc-card-foot {
    flex-wrap: wrap;
  }

  .sc-price {
    font-size: 1rem;
  }

  .sc-stock {
    left: 6px;
    bottom: 6px;
    font-size: .68rem;
    padding: 2px 8px;
  }

  .sc-btn-round {
    width: 34px;
    height: 34px;
  }

  .sc-stepper button {
    width: 28px;
    height: 28px;
  }

  .sc-stepper span {
    min-width: 1.5rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sc-item,
  .sc-combo,
  .sc-btn-round {
    transition: none;
  }
}
</style>
